// src/automation/components/AutomationAccessTab.js
//
// Access management for the Automation console — mirrors the WBR
// AccessManagementTab, but access is PER-TASK (allowed_tasks) and the roles are
// Admin / Contributor / Viewer:
//   admin        — full access + manage users
//   contributor  — view all; edit only the tasks ticked below
//   viewer       — view all, read-only
//
// Persists the flat access list to S3 via save_access_list.

import React, { useState, useEffect, useMemo } from "react";
import { Plus, Trash2, Save, Edit3, Users, X, Lock } from "lucide-react";
import { getAutoAccessList, saveAutoAccessList } from "../api/automation_data";
import { CollapsibleSection, Toast } from "wbr/components/admin/AdminShared";

// `scopeTasks`: null for the admin (all tasks); for a contributor, the tasks
// they hold — they can only grant/revoke those (the backend enforces the same).
export default function AutomationAccessTab({ sections = [], canManageUsers, isAdmin = false, scopeTasks = null }) {
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [search, setSearch] = useState("");

  // Flatten sections -> [{ name, label }] for the per-task checklist, limited to
  // the caller's scope (contributors only see the tasks they hold).
  const tasks = useMemo(() => {
    const out = [];
    for (const s of sections) for (const t of s.tasks || []) out.push({ name: t.name, label: t.label });
    return scopeTasks ? out.filter((t) => scopeTasks.includes(t.name)) : out;
  }, [sections, scopeTasks]);
  const inScope = (name) => !scopeTasks || scopeTasks.includes(name);

  useEffect(() => {
    setLoading(true);
    getAutoAccessList()
      .then((data) =>
        // Admin is code-only now: show any legacy "admin" entry as contributor.
        setAccessList(
          (Array.isArray(data) ? data : []).map((u) => (u.role === "admin" ? { ...u, role: "contributor" } : u))
        )
      )
      .catch(() => setAccessList([]))
      .finally(() => setLoading(false));
  }, []);

  const terms = useMemo(
    () => search.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
    [search]
  );
  const visible = useMemo(() => {
    if (!terms.length) return accessList.map((_, i) => i);
    return accessList.reduce((acc, u, i) => {
      if (terms.some((t) => (u.login || "").toLowerCase().includes(t))) acc.push(i);
      return acc;
    }, []);
  }, [accessList, terms]);

  const addUser = () => {
    setAccessList((p) => [{ login: "", role: "viewer", allowed_tasks: [], access_till: "" }, ...p]);
    setEditingIndex(0);
    setSearch("");
  };
  const removeUser = (i) => {
    const u = accessList[i];
    const outside = (u.allowed_tasks || []).filter((t) => !inScope(t));
    if (outside.length) {
      alert(`You can't remove ${u.login}: they hold tasks outside your access (${outside.join(", ")}).`);
      return;
    }
    setAccessList((p) => p.filter((_, j) => j !== i));
    if (editingIndex === i) setEditingIndex(null);
  };
  const updateUser = (i, field, value) =>
    setAccessList((p) =>
      p.map((u, j) => {
        if (j !== i) return u;
        const next = { ...u, [field]: value };
        if (field === "role" && value !== "contributor") next.allowed_tasks = [];
        return next;
      })
    );
  const toggleTask = (i, name) =>
    setAccessList((p) =>
      p.map((u, j) => {
        if (j !== i) return u;
        const cur = u.allowed_tasks || [];
        return { ...u, allowed_tasks: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] };
      })
    );
  // Select/Clear all only touches tasks in scope; grants made by others stay.
  const setAll = (i, all) =>
    setAccessList((p) =>
      p.map((u, j) => {
        if (j !== i) return u;
        const kept = (u.allowed_tasks || []).filter((t) => !inScope(t));
        return { ...u, allowed_tasks: all ? [...kept, ...tasks.map((t) => t.name)] : kept };
      })
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAutoAccessList(accessList);
      setToast({ message: "Access list saved!", type: "success" });
      setEditingIndex(null);
    } catch (e) {
      setToast({ message: e.message, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!canManageUsers) {
    return (
      <div className="admin-tab-content">
        <div className="admin-permission-notice">
          <Lock size={20} />
          <div><h4>View Only</h4><p>Contact an admin to manage access.</p></div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="admin-tab-content"><p>Loading access list…</p></div>;

  return (
    <div className="admin-tab-content">
      <CollapsibleSection
        title="User Access"
        subtitle="Grant view or per-task edit rights"
        actions={
          <>
            <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={addUser}>
              <Plus size={12} /> Add User
            </button>
            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={handleSave} disabled={saving}>
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="admin-access-search">
          <div className="admin-access-search__input-wrap">
            <Users size={14} className="admin-access-search__icon" />
            <input
              className="admin-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by login (comma-separated)…"
            />
            {search && (
              <button className="admin-btn-icon admin-access-search__clear" onClick={() => setSearch("")}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {visible.map((index) => {
          const u = accessList[index];
          const isEditing = editingIndex === index;
          const isContrib = u.role === "contributor";
          const count = (u.allowed_tasks || []).length;
          return (
            <div className={`admin-access-user ${isEditing ? "admin-access-user--editing" : ""}`} key={index}>
              <div className="admin-access-user__row">
                <div className="admin-access-user__fields">
                  <input
                    className="admin-input admin-input--table"
                    value={u.login}
                    onChange={(e) => updateUser(index, "login", e.target.value)}
                    placeholder="login"
                    autoFocus={index === 0 && !u.login}
                  />
                  <input
                    className="admin-input admin-input--table"
                    type="date"
                    value={u.access_till || ""}
                    onChange={(e) => updateUser(index, "access_till", e.target.value)}
                    title="Access granted till this date"
                  />
                  <select
                    className="admin-input admin-select admin-input--table"
                    value={u.role}
                    onChange={(e) => updateUser(index, "role", e.target.value)}
                  >
                    <option value="contributor">Contributor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  {isContrib && (
                    <span className="admin-access-user__item-count">
                      {count}/{tasks.length} tasks
                    </span>
                  )}
                </div>
                <div className="admin-access-user__actions">
                  {isContrib && (
                    <button
                      className="admin-btn-icon"
                      onClick={() => setEditingIndex(isEditing ? null : index)}
                      title="Edit allowed tasks"
                    >
                      <Edit3 size={13} />
                    </button>
                  )}
                  <button className="admin-btn-icon admin-btn-icon--danger" onClick={() => removeUser(index)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {isEditing && isContrib && (
                <div className="admin-access-user__items">
                  <div className="admin-access-user__items-header">
                    <span className="admin-access-user__items-title">
                      Allowed Tasks ({count} selected)
                    </span>
                    <div className="admin-access-user__items-actions">
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => setAll(index, true)}>
                        Select All
                      </button>
                      <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={() => setAll(index, false)}>
                        Clear All
                      </button>
                    </div>
                  </div>
                  <div className="admin-access-user__items-grid">
                    {tasks.map((t) => {
                      const checked = (u.allowed_tasks || []).includes(t.name);
                      return (
                        <label
                          className={`admin-access-item-checkbox ${checked ? "admin-access-item-checkbox--checked" : ""}`}
                          key={t.name}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggleTask(index, t.name)} />
                          <span className="admin-access-item-checkbox__label">{t.label}</span>
                          <span className="admin-access-item-checkbox__meta">{t.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(() => {
                    const others = (u.allowed_tasks || []).filter((t) => !inScope(t)).length;
                    return others ? (
                      <p className="admin-field__hint">
                        + {others} more task{others === 1 ? "" : "s"} granted outside your access (unchanged).
                      </p>
                    ) : null;
                  })()}
                  <p className="admin-field__hint">
                    Contributors can edit config, mappings and report design, and run, only the selected tasks.
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {accessList.length === 0 && (
          <div className="admin-empty-state admin-empty-state--sm">
            <p>No users configured</p>
            <button className="admin-btn admin-btn--sm admin-btn--outline" onClick={addUser}>
              <Plus size={12} /> Add User
            </button>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Role Reference" defaultOpen={false}>
        <div className="admin-permissions-grid">
          <div className="admin-permission-card">
            <div className="admin-permission-card__badge admin-permission-card__badge--admin">Admin</div>
            <ul className="admin-permission-card__list">
              <li>Set in the Lambda (not grantable here)</li>
              <li>Everything, incl. Auth tab + Activity Log</li>
              <li>Delete / restore any automation</li>
            </ul>
          </div>
          <div className="admin-permission-card">
            <div className="admin-permission-card__badge admin-permission-card__badge--editor">Contributor</div>
            <ul className="admin-permission-card__list">
              <li>Admin panel: Access + Automations</li>
              <li>Edit + run allowed tasks; add new automations</li>
              <li>Give others access to their own tasks</li>
            </ul>
          </div>
          <div className="admin-permission-card">
            <div className="admin-permission-card__badge admin-permission-card__badge--viewer">Viewer</div>
            <ul className="admin-permission-card__list">
              <li>View everything (read-only)</li>
              <li>No edits or runs</li>
              <li>No admin panel</li>
            </ul>
          </div>
        </div>
      </CollapsibleSection>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
